import { Component, OnInit, OnDestroy } from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormGroup,
    Validators,
    AbstractControl,
    ValidationErrors
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FarmService } from '@services/farm.service';
import { LoaderService } from '@services/loader.service';
import { SupplierService } from '@services/supplier.service';
import { ToastrService } from 'ngx-toastr';
import { Observable, forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-edit-farm',
    templateUrl: './edit-farm.component.html',
    styleUrls: ['./edit-farm.component.scss']
})
export class EditFarmComponent implements OnInit, OnDestroy {
    farmForm!: FormGroup;
    availableSensors: any[] = [];
    birdTypes: any[] = [];
    // BREED - COMMENTED OUT
    // breeds: any[] = [];
    isLoading$!: Observable<boolean>;
    isAuthLoading = false;

    private destroy$ = new Subject<void>();

    private nameValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null;
        }
        const value = control.value.toString().trim();
        if (!/[a-zA-Z]/.test(value)) {
            return { invalidName: true };
        }
        return null;
    }

    private positiveNumberValidator(
        control: AbstractControl
    ): ValidationErrors | null {
        if (!control.value && control.value !== 0) {
            return null;
        }
        const value = parseFloat(control.value);
        if (isNaN(value) || value <= 0) {
            return { invalidNumber: true };
        }
        return null;
    }

    private houseNameValidator(
        control: AbstractControl
    ): ValidationErrors | null {
        if (!control.value) {
            return null;
        }
        const name = control.value.toString().trim().toLowerCase();
        if (name === '0' || name === 'house 0') {
            return { reservedName: true };
        }
        return null;
    }

    constructor(
        private fb: FormBuilder,
        private farmService: FarmService,
        private route: ActivatedRoute,
        private supplierService: SupplierService,
        private toastr: ToastrService,
        private router: Router,
        private loaderService: LoaderService
    ) { }

    ngOnInit(): void {
        const farmId = +this.route.snapshot.params['id'];

        this.buildForm();
        this.setupFarmSizeCalculation();

        forkJoin({
            farm: this.farmService.getFarmDetail(farmId),
            // SENSORS - HIDE UI
            // sensors: this.farmService.getSensorsList(),
            birdTypes: this.supplierService.getBirdTypeList()
            // BREED - COMMENTED OUT
            // breeds: this.supplierService.getBreedList()
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ farm, birdTypes }) => {
                // SENSORS - HIDE UI
                // this.availableSensors = sensors;
                this.birdTypes = birdTypes;
                // BREED - COMMENTED OUT
                // this.breeds = breeds;
                this.patchFarmData(farm);
            });
    }

    setupFarmSizeCalculation(): void {
        this.farmForm
            .get('length')
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.calculateFarmSize();
            });

        this.farmForm
            .get('width')
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.calculateFarmSize();
            });
    }

    calculateFarmSize(): void {
        const length = parseFloat(this.farmForm.get('length')?.value) || 0;
        const width = parseFloat(this.farmForm.get('width')?.value) || 0;

        if (length > 0 && width > 0) {
            const size = length * width;
            this.farmForm.get('size')?.setValue(size, { emitEvent: false });
        } else {
            this.farmForm.get('size')?.setValue(0, { emitEvent: false });
        }
    }

    private buildForm(): void {
        this.farmForm = this.fb.group({
            id: [null],
            name: ['', [Validators.required, this.nameValidator.bind(this)]],
            type: [null, Validators.required],
            // BREED - COMMENTED OUT
            // breedId: [null, Validators.required],
            size: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
            length: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
            width: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],            
            waterTank: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],           
            dieselTank: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
            waterTankHeight: [
                null, 
                [Validators.required, Validators.min(0.01)]
            ],
            dieselTankHeight: [
                null,
                [Validators.required, Validators.min(0.01)]
            ],
            address: ['', [Validators.required, this.nameValidator.bind(this)]],
            farmHouses: this.fb.array([])
        });
    }

    private patchFarmData(farm: any): void {
        this.farmForm.patchValue(farm);

        const farmHousesArray = this.farmHouses;
        farmHousesArray.clear();

        farm.farmHouses.forEach((house: any) => {
            farmHousesArray.push(this.createHouse(house));
        });
    }

    private createHouse(house?: any): FormGroup {
        const isHouse0 = house?.name?.toLowerCase() === 'house 0';

        return this.fb.group({
            id: [house?.id || 0],
            name: [
                house?.name || '',
                isHouse0
                    ? []
                    : [
                        Validators.required,
                        this.nameValidator.bind(this),
                        this.houseNameValidator.bind(this)
                    ]
            ],
            length: [
                house?.length ?? 0,
                isHouse0
                    ? []
                    : [
                        Validators.required,
                        this.positiveNumberValidator.bind(this)
                    ]
            ],
            width: [
                house?.width ?? 0,
                isHouse0
                    ? []
                    : [
                        Validators.required,
                        this.positiveNumberValidator.bind(this)
                    ]
            ],
            feedingSiloCapacity: [
                house?.feedingSiloCapacity ?? 0,
                isHouse0
                    ? []
                    : [
                        Validators.required,
                        this.positiveNumberValidator.bind(this)
                    ]
            ],
            feedingSiloHeight: [
                house?.feedingSiloHeight ?? 0,
                isHouse0
                    ? []
                    : [
                        Validators.required,
                        this.positiveNumberValidator.bind(this)
                    ]
            ],

            farmHousePartitions: this.fb.array(
                (house?.farmHousePartitions || []).map((p: any) =>
                    this.createPartition(p, isHouse0)
                )
            )
        });
    }

    private createPartition(
        partition?: any,
        isHouse0: boolean = false
    ): FormGroup {
        return this.fb.group({
            id: [partition?.id || 0],
            name: [
                partition?.name || '',
                isHouse0
                    ? []
                    : [Validators.required, this.nameValidator.bind(this)]
            ],
            length: [
                partition?.length ?? 0,
                isHouse0
                    ? []
                    : [
                        Validators.required,
                        this.positiveNumberValidator.bind(this)
                    ]
            ],
            capacity: [
                partition?.capacity ?? 0,
                isHouse0
                    ? []
                    : [
                        Validators.required,
                        this.positiveNumberValidator.bind(this)
                    ]
            ],
            capacityStatus: [partition?.capacityStatus || false],
            sensors: [partition?.sensors?.map((s: any) => s.id) || []]
        });
    }

    get farmHouses(): FormArray {
        return this.farmForm.get('farmHouses') as FormArray;
    }

    getPartitions(houseIndex: number): FormArray {
        return this.farmHouses
            .at(houseIndex)
            .get('farmHousePartitions') as FormArray;
    }

    addFarmHouse(): void {
        this.farmHouses.push(this.createHouse());
    }

    addPartition(houseIndex: number): void {
        this.getPartitions(houseIndex).push(this.createPartition());
    }

    removeFarmHouse(index: number): void {
        const houseName = this.farmHouses.at(index).get('name')?.value;
        if (houseName?.toLowerCase() === 'house 0') {
            return;
        }
        this.farmHouses.removeAt(index);
    }

    removePartition(houseIndex: number, partitionIndex: number): void {
        this.getPartitions(houseIndex).removeAt(partitionIndex);
    }

    onSubmit(): void {
        this.loaderService.showLoader();
        this.isAuthLoading = true;
        this.markFormGroupTouched(this.farmForm);

        if (this.farmForm.invalid) {
            this.showError('Please fill in all required fields correctly.');
            return;
        }

        const formData = { ...this.farmForm.value };
        const sensorMap = new Map(this.availableSensors.map((s) => [s.id, s]));

        formData.farmHouses.forEach((house: any, index: number) => {
            if (index === 0 && house.name?.toLowerCase() === 'house 0') {
                house.length = house.length || 0;
                house.width = house.width || 0;

                if (
                    house.farmHousePartitions &&
                    house.farmHousePartitions.length > 0
                ) {
                    const partition0 = house.farmHousePartitions[0];
                    if (partition0.name?.toLowerCase() === 'partition 0') {
                        partition0.length = partition0.length || 0;
                        partition0.capacity = partition0.capacity || 0;
                    }
                }
            }

            house.farmHousePartitions.forEach((partition: any) => {
                partition.sensors = partition.sensors.map((id: number) =>
                    sensorMap.get(id)
                );
            });
        });

        this.farmService
            .updaeFarmDetail(formData)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.isAuthLoading = false;
                    this.loaderService.hideLoader();
                    this.toastr.success('Farm updated successfully!');
                    this.router.navigate(['/farms-information']);
                },
                () => { }
            );
    }

    private markFormGroupTouched(formGroup: FormGroup | FormArray): void {
        Object.values(formGroup.controls).forEach((control) => {
            if (control instanceof FormGroup || control instanceof FormArray) {
                this.markFormGroupTouched(control);
            } else {
                control.markAsTouched();
            }
        });
    }

    private showError(message: string): void {
        this.isAuthLoading = false;
        this.loaderService.hideLoader();
        this.toastr.error(message);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
